import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, ArrowRight, CheckCircle, AlertCircle, Send, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import ShareResults from '../components/ShareResults';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGE_RANGES = [
  { value: '18-24', label: '18-24 years' },
  { value: '25-34', label: '25-34 years' },
  { value: '35-44', label: '35-44 years' },
  { value: '45-54', label: '45-54 years' },
  { value: '55+', label: '55 years and above' }
];

const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Prefer not to say' }
];

const SECTORS = [
  { value: 'student', label: 'Student' },
  { value: 'worker', label: 'Worker (Employed)' },
  { value: 'trader', label: 'Trader / Business Owner' },
  { value: 'professional', label: 'Professional' },
  { value: 'unemployed', label: 'Unemployed' },
  { value: 'other', label: 'Other' }
];

function generateFingerprint() {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('fingerprint', 2, 2);
  
  const data = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
    canvas.toDataURL()
  ].join('|');
  
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
}

export default function PublicSurvey() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  
  const [survey, setSurvey] = useState(null);
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const [step, setStep] = useState(0); // 0 = demographics, 1+ = questions
  const [demographics, setDemographics] = useState({
    state: '',
    lga: '',
    age_range: '',
    gender: '',
    sector: ''
  });
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    fetchSurvey();
    fetchStates();
  }, [surveyId]);

  useEffect(() => {
    if (demographics.state) {
      fetchLgas(demographics.state);
    }
  }, [demographics.state]);

  const fetchSurvey = async () => {
    try {
      const response = await fetch(`${API}/surveys/${surveyId}`);
      if (response.ok) {
        const data = await response.json();
        setSurvey(data);
        
        // Check if survey is active
        const now = new Date();
        const start = new Date(data.start_date);
        const end = new Date(data.end_date);
        
        if (!data.is_active || now < start || now > end) {
          setError('This survey is not currently active.');
        }
      } else {
        setError('Survey not found.');
      }
    } catch (err) {
      console.error('Error fetching survey:', err);
      setError('Failed to load survey.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await fetch(`${API}/nigeria/states`);
      if (response.ok) {
        const data = await response.json();
        setStates(data);
      }
    } catch (err) {
      console.error('Error fetching states:', err);
    }
  };

  const fetchLgas = async (stateCode) => {
    try {
      const response = await fetch(`${API}/nigeria/states/${stateCode}/lgas`);
      if (response.ok) {
        const data = await response.json();
        setLgas(data.lgas || []);
      }
    } catch (err) {
      console.error('Error fetching LGAs:', err);
    }
  };

  const handleStateChange = (value) => {
    setDemographics({ ...demographics, state: value, lga: '' });
  };

  const totalSteps = survey ? survey.questions.length + 1 : 1;
  const progress = ((step + 1) / totalSteps) * 100;

  const canProceed = () => {
    if (step === 0) {
      return demographics.state && demographics.lga;
    }
    const question = survey.questions[step - 1];
    if (question.required) {
      return answers[question.question_id] !== undefined && answers[question.question_id] !== '';
    }
    return true;
  };

  const handleNext = () => {
    if (canProceed()) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    if (!canProceed()) return;

    setSubmitting(true);
    try {
      const selectedState = states.find(s => s.code === demographics.state);
      
      const payload = {
        survey_id: surveyId,
        state: selectedState?.name || demographics.state,
        lga: demographics.lga,
        age_range: demographics.age_range || null,
        gender: demographics.gender || null,
        sector: demographics.sector || null,
        answers: Object.entries(answers).map(([question_id, answer]) => ({
          question_id,
          answer
        })),
        device_fingerprint: generateFingerprint()
      };

      const response = await fetch(`${API}/responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit response');
      }
    } catch (err) {
      console.error('Error submitting response:', err);
      toast.error('Failed to submit response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Survey Unavailable</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/">
              <Button>Return Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full" data-testid="submission-success">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
            <p className="text-muted-foreground mb-6">
              Your response has been recorded. Your voice matters in shaping Nigeria's future.
            </p>
            <div className="space-y-3">
              <Link to={`/results/${surveyId}`}>
                <Button className="w-full">View Results</Button>
              </Link>
              <div className="flex justify-center">
                <ShareResults 
                  surveyId={surveyId}
                  surveyTitle={survey?.title}
                  totalResponses={(survey?.response_count || 0) + 1}
                />
              </div>
              <Link to="/">
                <Button variant="outline" className="w-full">Return Home</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-survey">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
                <span className="text-white font-bold text-xs">CV</span>
              </div>
              <span className="font-semibold text-sm">Citizen Voice</span>
            </Link>
            <Badge variant="secondary">{survey.policy_category}</Badge>
          </div>
          <Progress value={progress} className="h-1" />
          <p className="text-xs text-muted-foreground mt-2">
            Step {step + 1} of {totalSteps}
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">{survey.title}</CardTitle>
            {step === 0 && (
              <CardDescription>{survey.description}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {step === 0 ? (
              // Demographics Step
              <div className="space-y-6" data-testid="demographics-step">
                <div>
                  <Label className="text-base font-medium">
                    State <span className="text-destructive">*</span>
                  </Label>
                  <Select value={demographics.state} onValueChange={handleStateChange}>
                    <SelectTrigger className="mt-2" data-testid="state-select">
                      <SelectValue placeholder="Select your state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-base font-medium">
                    Local Government Area (LGA) <span className="text-destructive">*</span>
                  </Label>
                  <Select 
                    value={demographics.lga} 
                    onValueChange={(value) => setDemographics({ ...demographics, lga: value })}
                    disabled={!demographics.state}
                  >
                    <SelectTrigger className="mt-2" data-testid="lga-select">
                      <SelectValue placeholder="Select your LGA" />
                    </SelectTrigger>
                    <SelectContent>
                      {lgas.map((lga) => (
                        <SelectItem key={lga} value={lga}>
                          {lga}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="border-t pt-6">
                  <p className="text-sm text-muted-foreground mb-4">
                    Optional: Help us understand who is responding
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Age Range</Label>
                      <Select 
                        value={demographics.age_range} 
                        onValueChange={(value) => setDemographics({ ...demographics, age_range: value })}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select age range" />
                        </SelectTrigger>
                        <SelectContent>
                          {AGE_RANGES.map((range) => (
                            <SelectItem key={range.value} value={range.value}>
                              {range.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Gender</Label>
                      <Select 
                        value={demographics.gender} 
                        onValueChange={(value) => setDemographics({ ...demographics, gender: value })}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          {GENDERS.map((g) => (
                            <SelectItem key={g.value} value={g.value}>
                              {g.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Sector</Label>
                      <Select 
                        value={demographics.sector} 
                        onValueChange={(value) => setDemographics({ ...demographics, sector: value })}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select sector" />
                        </SelectTrigger>
                        <SelectContent>
                          {SECTORS.map((s) => (
                            <SelectItem key={s.value} value={s.value}>
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Question Step
              <div className="space-y-6" data-testid={`question-step-${step}`}>
                {(() => {
                  const question = survey.questions[step - 1];
                  return (
                    <div>
                      <Label className="text-lg font-medium block mb-4">
                        {question.text}
                        {question.required && <span className="text-destructive ml-1">*</span>}
                      </Label>

                      {question.question_type === 'multiple_choice' && (
                        <RadioGroup
                          value={answers[question.question_id] || ''}
                          onValueChange={(value) => setAnswers({ ...answers, [question.question_id]: value })}
                          className="space-y-3"
                        >
                          {question.options.map((option) => (
                            <label 
                              key={option.option_id}
                              className="flex items-center space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                            >
                              <RadioGroupItem value={option.option_id} />
                              <span>{option.text}</span>
                            </label>
                          ))}
                        </RadioGroup>
                      )}

                      {question.question_type === 'rating' && (
                        <div className="flex justify-center gap-4">
                          {[1, 2, 3, 4, 5].map((rating) => (
                            <button
                              key={rating}
                              onClick={() => setAnswers({ ...answers, [question.question_id]: rating })}
                              className={`w-14 h-14 rounded-lg border-2 text-lg font-semibold transition-all ${
                                answers[question.question_id] === rating
                                  ? 'bg-primary text-white border-primary'
                                  : 'border-border hover:border-primary/50'
                              }`}
                            >
                              {rating}
                            </button>
                          ))}
                        </div>
                      )}

                      {question.question_type === 'open_ended' && (
                        <Textarea
                          value={answers[question.question_id] || ''}
                          onChange={(e) => setAnswers({ ...answers, [question.question_id]: e.target.value })}
                          placeholder="Type your response here..."
                          rows={4}
                          className="w-full"
                        />
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={step === 0}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>

              {step < totalSteps - 1 ? (
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  data-testid="next-btn"
                >
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!canProceed() || submitting}
                  data-testid="submit-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                  <Send className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Privacy Notice */}
        <p className="text-xs text-center text-muted-foreground mt-6 px-4">
          Your response is anonymous. We do not collect personal identifying information.
          This is citizen sentiment data, not election results.
        </p>
      </main>
    </div>
  );
}
